import { get } from "lodash";
import { eventChannel } from "redux-saga";
import { call, fork, put, select, take, takeEvery } from "redux-saga/effects";

import {
  IntegrationAuthType,
  OAUTH_CALLBACK_PATH,
  TokenScope,
  getAuthId,
  isPkceEnabled,
  hasUnresolvedTenant,
  resolveOpenApiTenantUrl,
} from "@superblocksteam/shared";
import type {
  AuthConfig,
  AuthType,
  ButtonClickPayload,
  ClientAuthMethod,
  DatasourceAuthState,
  LoadButtonPayloadInit,
} from "@superblocksteam/shared";

import { generateOneTimeCode } from "core/utils/crypto";

import { encodeOAuthRedirectState } from "../../../core/utils/oauth-state";
import {
  addPkceChallengeToUrl,
  generatePkceVerifier,
  hasPkceClientSecret,
} from "../../../core/utils/pkce";
import { openOAuthPopupAndWaitForCompletion } from "../../pages/components/oauth-callback-shell-handshake";
import {
  selectV2ApiById,
  selectV2ApiMetaById,
  type ExecutionResponse,
} from "../../store/slices/apisV2";
import { getV2ApiName } from "../../store/slices/apisV2/utils/getApiIdAndName";
import {
  getConnectedTokensSaga,
  selectHasConnectedTokens,
} from "../../store/slices/datasources";
import {
  revokeAndDeleteSharedConnectedTokens,
  revokeAndDeleteUserConnectedTokens,
} from "../../store/slices/datasources/client";
import { callSagas } from "../../store/utils/saga";
import {
  loadDynamicFormButtonInit,
  loadDynamicFormButtonSuccess,
} from "../actions/dynamicFormActions";
import {
  ReduxActionTypes,
  ReduxActionErrorTypes,
} from "../constants/ReduxActionConstants";
import type { ReduxAction } from "../constants/ReduxActionConstants";

function* loadButtonSaga(actionPayload: ReduxAction<LoadButtonPayloadInit>) {
  try {
    const integrationConfigurationId =
      actionPayload.payload.eventAttributes.integrationConfigurationId;
    const integrationId = actionPayload.payload.eventAttributes.integrationId;
    const isNew = actionPayload.payload.eventAttributes.isNew;
    const authId = getAuthId(
      actionPayload.payload.eventAttributes.authType,
      actionPayload.payload.eventAttributes.authConfig,
      integrationId,
      integrationConfigurationId,
    );
    const tokenScope = actionPayload.payload.eventAttributes.authConfig
      ?.tokenScope as TokenScope;
    //TODO(alex): IntegrationForm calls getConnectedTokens saga, but there is no guarantee that the tokens are loaded and Redux state is update at this point,
    // so we need to wait for the tokens to be loaded before we can determine if the button should be disabled or not.
    // calling getConnectedTokensSaga here, which is inefficient, but it works for now.
    //yield take(ReduxActionTypes.LOAD_CONNECTED_TOKENS_SUCCESS);
    if (!isNew) {
      yield callSagas([
        getConnectedTokensSaga.apply({
          integrationId: integrationId,
          integrationConfigurationId: integrationConfigurationId,
          authId: authId,
          tokenScope: tokenScope,
        }),
      ]);
    }
    const hasConnectedTokens: boolean = yield select(
      selectHasConnectedTokens,
      integrationConfigurationId,
      integrationId,
    );
    switch (actionPayload.payload.buttonType) {
      case "connectOAuth": {
        // if the user has already connected to this integration or the token is not shared, disable the button
        const dynamicFormButtonDisabled =
          hasConnectedTokens || tokenScope === TokenScope.USER;
        yield put(
          loadDynamicFormButtonSuccess({
            buttonType: "connectOAuth",
            disabled: dynamicFormButtonDisabled,
          }),
        );
        break;
      }
      case "revokeOAuthTokens": {
        // no tokens to revoke, disable the button
        const dynamicFormButtonDisabled = !hasConnectedTokens;
        yield put(
          loadDynamicFormButtonSuccess({
            buttonType: "revokeOAuthTokens",
            disabled: dynamicFormButtonDisabled,
          }),
        );
        break;
      }
      default:
        yield put({
          type: ReduxActionErrorTypes.DYNAMIC_FORM_BUTTON_CLICK_ERROR,
          payload: {
            error: new Error(
              `Unknown button type: ${actionPayload.payload.buttonType}`,
            ),
          },
        });
    }
  } catch (error: any) {
    yield put({
      type: ReduxActionErrorTypes.DYNAMIC_FORM_BUTTON_CLICK_ERROR,
      payload: {
        error,
      },
    });
  }
}

export function* buttonClickSaga(
  actionPayload: ReduxAction<ButtonClickPayload>,
): Generator<any, any, any> {
  try {
    switch (actionPayload.payload.buttonType) {
      case "revokeOAuthTokens": {
        try {
          const authId = getAuthId(
            actionPayload.payload.eventAttributes.authType,
            actionPayload.payload.eventAttributes.authConfig,
            actionPayload.payload.eventAttributes.integrationId,
            actionPayload.payload.eventAttributes.configurationId,
          );
          const revokeTokenUrl = resolveOpenApiTenantUrl(
            actionPayload.payload.eventAttributes.authConfig?.revokeTokenUrl as
              | string
              | undefined,
            actionPayload.payload.eventAttributes.openApiTenantName,
          ) as string;
          const result: boolean =
            actionPayload.payload.eventAttributes.authConfig?.tokenScope ===
            TokenScope.DATASOURCE
              ? yield call(
                  revokeAndDeleteSharedConnectedTokens,
                  authId,
                  revokeTokenUrl,
                )
              : yield call(
                  revokeAndDeleteUserConnectedTokens,
                  authId,
                  revokeTokenUrl,
                );
          if (!result) {
            throw new Error(
              `Failed to revoke tokens: ${JSON.stringify(result)}`,
            );
          }
          yield put({
            type: ReduxActionTypes.DYNAMIC_FORM_BUTTON_CLICK_SUCCESS,
            payload: actionPayload.payload,
          });
        } catch (error: any) {
          yield put({
            type: ReduxActionErrorTypes.DYNAMIC_FORM_BUTTON_CLICK_ERROR,
            payload: {
              buttonType: actionPayload.payload.buttonType,
              error: `Failed to revoke tokens: ${error?.message}`,
            },
          });
        }
        break;
      }
      case "connectOAuth": {
        let openedWindow: Window | null | undefined;
        try {
          const eventAttributes = actionPayload.payload.eventAttributes;
          const {
            integrationId,
            configurationId,
            pluginId,
            accessType,
            stateConfigExclude,
            responseType,
            owner,
            additionalUrlParams,
          } = actionPayload.payload.eventAttributes;
          // A provider that hosts each customer on their own subdomain stores these with the
          // tenant placeholder, so resolve before they are used or handed to the server.
          const tokenUrl = resolveOpenApiTenantUrl(
            eventAttributes?.authConfig?.tokenUrl,
            eventAttributes?.openApiTenantName,
          );
          const authorizationUrl = resolveOpenApiTenantUrl(
            eventAttributes?.authConfig?.authorizationUrl,
            eventAttributes?.openApiTenantName,
          );
          if (
            hasUnresolvedTenant(authorizationUrl) ||
            hasUnresolvedTenant(tokenUrl)
          ) {
            // Opening the popup would only show a browser error for an unresolvable host.
            throw new Error(
              "the subdomain is missing or invalid (letters, digits and hyphens only)",
            );
          }
          const userInfo = eventAttributes?.authConfig?.userInfoUrl;
          const dynamicWorkflowConfiguration =
            eventAttributes?.dynamicWorkflowConfiguration;
          const workflowId = dynamicWorkflowConfiguration?.workflowId;
          const workflowResult = yield select((state) =>
            selectV2ApiMetaById(state, workflowId),
          );
          const workflowExecutionResult: ExecutionResponse | undefined =
            workflowResult?.executionResult;
          const workflow = yield select((state) =>
            selectV2ApiById(state, workflowId),
          );
          const workflowName = workflow ? getV2ApiName(workflow) : undefined;
          const fetchCredentialsDynamically =
            dynamicWorkflowConfiguration?.enabled ?? false;
          const clientId = resolveBindingsFromWorkflowIfNeeded(
            eventAttributes?.authConfig?.clientId,
            workflowExecutionResult,
            workflowName,
            fetchCredentialsDynamically,
          );
          const clientSecret = resolveBindingsFromWorkflowIfNeeded(
            eventAttributes?.authConfig?.clientSecret,
            workflowExecutionResult,
            workflowName,
            fetchCredentialsDynamically,
          );
          const clientAuthMethod =
            eventAttributes?.authConfig?.clientAuthMethod;
          const scope = resolveBindingsFromWorkflowIfNeeded(
            eventAttributes?.authConfig?.scope,
            workflowExecutionResult,
            workflowName,
            fetchCredentialsDynamically,
          );
          const refreshTokenFromServer =
            eventAttributes?.authConfig?.refreshTokenFromServer;
          const usePkce = isPkceEnabled(eventAttributes?.authConfig?.usePkce);
          const usePkceClientSecret = hasPkceClientSecret(clientSecret);
          const tokenScope =
            eventAttributes?.authConfig?.tokenScope ?? TokenScope.DATASOURCE;
          const oneTimeCode = generateOneTimeCode();
          let stateObj: Partial<DatasourceAuthState> = {
            oneTimeCode,
            integrationId,
          };

          const dsConfigState = {
            authConfig: {
              refreshTokenFromServer: refreshTokenFromServer,
              clientId: clientId as string,
              clientSecret: clientSecret as string,
              clientAuthMethod: clientAuthMethod as ClientAuthMethod,
              tokenUrl: tokenUrl as string,
              authorizationUrl: authorizationUrl as string,
              userInfoUrl: userInfo as string,
              tokenScope,
              scope: scope as string | undefined,
              usePkce,
              usePkceClientSecret,
            },
            authType: IntegrationAuthType.OAUTH2_CODE,
            authId: getAuthId(
              IntegrationAuthType.OAUTH2_CODE,
              eventAttributes?.authConfig,
              integrationId,
              configurationId,
            ),
            pluginId,
            origin: window.location.origin,
            integrationId,
            configurationId,
          };

          const fullState = { ...stateObj, ...dsConfigState };
          if (!stateConfigExclude?.includes("datasource-auth-state")) {
            // Add the auth config metadata if it is not excluded.
            stateObj = {
              ...stateObj,
              ...dsConfigState,
            };
          }
          const redirectState = encodeOAuthRedirectState(stateObj);

          let href = `${authorizationUrl}?client_id=${clientId}&redirect_uri=${window.location.origin}/${OAUTH_CALLBACK_PATH}`;
          if (scope) {
            href = `${href}&scope=${scope}`;
          }
          href = `${href}&state=${redirectState}`;
          // The form's values win over the template's: the generic flow lets the user set
          // prompt and access_type per server (Google needs access_type=offline for a refresh
          // token), the fixed-endpoint templates declare them.
          const effectiveAccessType =
            eventAttributes?.authConfig?.accessType || accessType;
          if (effectiveAccessType) {
            href = `${href}&access_type=${encodeURIComponent(effectiveAccessType)}`;
          }
          if (responseType) {
            href = `${href}&response_type=${responseType}`;
          }
          const promptType = eventAttributes?.authConfig?.promptType;
          if (promptType) {
            href = `${href}&prompt=${encodeURIComponent(promptType)}`;
          }
          if (owner) {
            href = `${href}&owner=${owner}`;
          }
          if (additionalUrlParams) {
            for (const [key, value] of Object.entries(additionalUrlParams)) {
              href = `${href}&${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`;
            }
          }
          href =
            resolveBindingsFromWorkflowIfNeeded(
              href,
              workflowExecutionResult,
              workflowName,
              fetchCredentialsDynamically,
            ) ?? href;
          let codeVerifier: string | undefined;
          if (usePkce) {
            openedWindow = window.open(
              "",
              "",
              "toolbar=no,status=no,menubar=no,location=center,scrollbars=no,height=600,width=610",
            );
            codeVerifier = generatePkceVerifier();
            const pkceAuthorizationUrl = new URL(href);
            yield call(
              addPkceChallengeToUrl,
              pkceAuthorizationUrl,
              codeVerifier,
            );
            href = pkceAuthorizationUrl.toString();
          }
          // A popup that goes dark (closed, or severed by the provider's
          // COOP header while still open) releases the button right away;
          // the attempt keeps waiting on the same-origin channel and only a
          // real completion surfaces after that.
          let released = false;
          let notifySeveredWait: () => void = () => undefined;
          const severedWait = eventChannel<boolean>((emit) => {
            notifySeveredWait = () => emit(true);
            return () => undefined;
          });
          yield fork(function* () {
            yield take(severedWait);
            released = true;
            yield put({
              type: ReduxActionErrorTypes.DYNAMIC_FORM_BUTTON_CLICK_ERROR,
              payload: {
                buttonType: actionPayload.payload.buttonType,
                error: undefined,
              },
            });
          });
          let succeded: boolean;
          let errorMessage: string | undefined;
          try {
            [succeded, errorMessage] = yield call(
              openOAuthPopupAndWaitForCompletion,
              {
                authConfig: fullState.authConfig,
                codeVerifier,
                onSeveredWait: () => notifySeveredWait(),
                openPopup: () => {
                  if (openedWindow) {
                    openedWindow.location.href = href;
                  } else {
                    openedWindow = window.open(
                      href,
                      "",
                      "toolbar=no,status=no,menubar=no,location=center,scrollbars=no,height=600,width=610",
                    );
                  }
                  return openedWindow;
                },
                pending: { integrationId, oneTimeCode },
                targetOrigin: window.location.origin,
              },
            );
          } finally {
            severedWait.close();
          }
          if (succeded) {
            yield put({
              type: ReduxActionTypes.DYNAMIC_FORM_BUTTON_CLICK_SUCCESS,
              payload: actionPayload.payload,
            });
          } else if (errorMessage === undefined) {
            // Silent outcome of a dark popup: nothing to show. The release
            // normally happened when the popup went dark; this is a safety
            // net so the button can never stay stuck.
            if (!released) {
              yield put({
                type: ReduxActionErrorTypes.DYNAMIC_FORM_BUTTON_CLICK_ERROR,
                payload: {
                  buttonType: actionPayload.payload.buttonType,
                  error: undefined,
                },
              });
            }
          } else {
            openedWindow?.close();
            yield put({
              type: ReduxActionErrorTypes.DYNAMIC_FORM_BUTTON_CLICK_ERROR,
              payload: {
                buttonType: actionPayload.payload.buttonType,
                error: errorMessage,
              },
            });
          }
        } catch (error: any) {
          openedWindow?.close();
          yield put({
            type: ReduxActionErrorTypes.DYNAMIC_FORM_BUTTON_CLICK_ERROR,
            payload: {
              buttonType: actionPayload.payload.buttonType,
              error: `Failed to connect to your account: ${error?.message}`,
            },
          });
        }
        break;
      }
      default:
        yield put({
          type: ReduxActionErrorTypes.DYNAMIC_FORM_BUTTON_CLICK_ERROR,
          payload: {
            error: new Error(`Unknown button type`),
          },
        });
    }
  } catch (error: any) {
    yield put({
      type: ReduxActionErrorTypes.DYNAMIC_FORM_BUTTON_CLICK_ERROR,
      payload: {
        error: `Unknown error when handling button click: ${error?.message}`,
      },
    });
  }
}

function* buttonClickSuccessSaga(
  actionPayload: ReduxAction<ButtonClickPayload>,
) {
  try {
    const integrationId = actionPayload.payload.eventAttributes.integrationId;
    const integrationConfigurationId =
      actionPayload.payload.eventAttributes.configurationId;
    const authType = actionPayload.payload.eventAttributes.authType as AuthType;
    const authConfig = actionPayload.payload.eventAttributes
      .authConfig as AuthConfig;
    const authId = getAuthId(
      authType,
      authConfig,
      integrationId,
      integrationConfigurationId,
    );
    const tokenScope = actionPayload.payload.eventAttributes.authConfig
      ?.tokenScope as TokenScope;
    switch (actionPayload.payload.buttonType) {
      case "connectOAuth": {
        if (integrationId && integrationConfigurationId) {
          yield callSagas([
            getConnectedTokensSaga.apply({
              integrationId: integrationId,
              integrationConfigurationId: integrationConfigurationId,
              authId,
              tokenScope,
            }),
          ]);
        }
        const isNew = actionPayload.payload.eventAttributes.isNew;
        // initiate loading the button again
        yield put(
          loadDynamicFormButtonInit({
            buttonType: "connectOAuth",
            eventAttributes: {
              integrationId,
              integrationConfigurationId,
              authType,
              authConfig,
              isNew,
            },
          }),
        );
        // initiate loading for all dependent buttons
        yield put(
          loadDynamicFormButtonInit({
            buttonType: "revokeOAuthTokens",
            eventAttributes: {
              integrationId,
              integrationConfigurationId,
              authType,
              authConfig,
              isNew,
            },
          }),
        );
        break;
      }
      case "revokeOAuthTokens": {
        const isNew = actionPayload.payload.eventAttributes.isNew;
        if (integrationId && integrationConfigurationId) {
          yield callSagas([
            getConnectedTokensSaga.apply({
              integrationId: integrationId,
              integrationConfigurationId: integrationConfigurationId,
              authId,
              tokenScope,
            }),
          ]);
          // initiate loading the button again
          yield put(
            loadDynamicFormButtonInit({
              buttonType: "revokeOAuthTokens",
              eventAttributes: {
                integrationId,
                integrationConfigurationId,
                authType: actionPayload.payload.eventAttributes.authType,
                authConfig: actionPayload.payload.eventAttributes.authConfig,
                isNew,
              },
            }),
          );
          // initiate loading for all dependent buttons
          yield put(
            loadDynamicFormButtonInit({
              buttonType: "connectOAuth",
              eventAttributes: {
                integrationId,
                integrationConfigurationId,
                authType,
                authConfig,
                isNew,
              },
            }),
          );
        }
        break;
      }
      default:
        yield put({
          type: ReduxActionErrorTypes.DYNAMIC_FORM_BUTTON_CLICK_ERROR,
          payload: {
            error: new Error(`Unknown button type`),
          },
        });
    }
  } catch (error: any) {
    yield put({
      type: ReduxActionErrorTypes.DYNAMIC_FORM_BUTTON_CLICK_ERROR,
      payload: {
        error,
      },
    });
  }
}

export default function* dynamicFormSagas() {
  yield takeEvery(
    ReduxActionTypes.LOAD_DYNAMIC_FORM_BUTTON_INIT,
    loadButtonSaga,
  );
  yield takeEvery(ReduxActionTypes.DYNAMIC_FORM_BUTTON_CLICK, buttonClickSaga);
  yield takeEvery(
    ReduxActionTypes.DYNAMIC_FORM_BUTTON_CLICK_SUCCESS,
    buttonClickSuccessSaga,
  );
}
function resolveBindingsFromWorkflowIfNeeded(
  sourceStringWithBindings: string | undefined,
  workflowExecutionResult: ExecutionResponse | undefined,
  workflowName: string | undefined,
  fetchCredentialsEnabled: boolean,
): string | undefined {
  if (!fetchCredentialsEnabled) {
    return sourceStringWithBindings;
  }
  const workflowOutput =
    workflowExecutionResult && !("systemError" in workflowExecutionResult)
      ? workflowExecutionResult.output?.result
      : undefined;
  sourceStringWithBindings = sourceStringWithBindings?.replace(
    /\{{(.*?)\}}/g,
    (_, path) => {
      const bindingPathInStepOutput = path.replace(
        `${workflowName}.response.`,
        "",
      );
      const bindingValue = get(workflowOutput, bindingPathInStepOutput);
      if (!bindingValue) {
        throw new Error(
          `Failed to resolve binding: {{${path}}}. Please make sure the workflow output has this property.`,
        );
      }
      return bindingValue;
    },
  );
  return sourceStringWithBindings;
}
